import * as THREE from 'three';
import { clamp, smoothstep } from '../core/Rand.js';
import { MovementConfig } from './MovementConfig.js';

/**
 * Movement — the capsule controller and the movement state machine.
 *
 * Runs entirely inside `fixed(dt)` at 120 Hz. It owns:
 *   feet position, velocity, capsule height, grounded/crouched/sprint/slide/
 *   mantle state, stamina, the footstep phase, and every gameplay event.
 *
 * It never touches the camera. `CameraRig` reads this and interpolates.
 */

const UP = new THREE.Vector3(0, 1, 0);

const _wish = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _delta = new THREE.Vector3();
const _centre = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _tmp2 = new THREE.Vector3();
const _probeOrigin = new THREE.Vector3();
const _hitPoint = new THREE.Vector3();
const _hitNormal = new THREE.Vector3();
const _topPoint = new THREE.Vector3();
const _topNormal = new THREE.Vector3();
const _spawnBase = new THREE.Vector3();
const _spawnCand = new THREE.Vector3();
const _down = new THREE.Vector3(0, -1, 0);

export class Movement {
  constructor(cfg = MovementConfig, adapter) {
    this.cfg = cfg;
    this.adapter = adapter;
    this.ctx = null;

    this.feet = new THREE.Vector3();
    this.prevFeet = new THREE.Vector3();
    this.lastSafe = new THREE.Vector3();
    this.spawn = new THREE.Vector3(0, 0, 0);
    this.velocity = new THREE.Vector3();
    this.groundNormal = new THREE.Vector3(0, 1, 0);

    this.yaw = 0;

    this.grounded = true;
    this.wasGrounded = true;
    this.crouched = false;
    this.sprinting = false;
    this.tacSprinting = false;
    this.sliding = false;
    this.mantling = false;
    this.hitWall = false;
    this.steppedUp = 0;

    this.height = cfg.standHeight;
    this.state = 'idle';
    this.speed = 0;
    this.stamina = cfg.staminaMax;
    this.adsBlend = 0;
    this._crouchHeld = false;
    this._sprintWasPressed = false;

    /* timers */
    this.coyote = 0;
    this.jumpBuffer = 0;
    this.jumpCooldown = 0;
    this.slideT = 0;
    this.slideCooldown = 0;
    this.mantleCooldown = 0;
    this.sprintTapTimer = 0;
    this.tacBlend = 0;
    this.staminaHold = 0;
    this.airTime = 0;
    this.lastVy = 0;
    this.fallPeakY = 0;
    this.lastFallHeight = 0;
    this.slideEntrySpeed = 0;
    this.slideDir = new THREE.Vector3(0, 0, -1);

    /* footsteps / bob phase (sim-side so it is deterministic) */
    this.bobPhase = 0;
    this.prevBobPhase = 0;
    this.stepIndex = 0;
    this.surface = 'concrete';
    this._surfaceTimer = 0;

    /* one-shot signals consumed by the camera rig */
    this.pendingLandImpact = 0;
    this.landedThisStep = false;

    /* mantle publish block (frozen shape, see report) */
    this.mantle = {
      active: false,
      kind: null,
      progress: 0,
      elapsed: 0,
      duration: 0,
      start: new THREE.Vector3(),
      target: new THREE.Vector3(),
      ledge: new THREE.Vector3(),
      normal: new THREE.Vector3(0, 0, 1),
      handL: new THREE.Vector3(),
      handR: new THREE.Vector3(),
      handWeight: 0,
      height: 0,
    };
    this._mantleExitCrouch = false;

    this.debugCounters = { respawns: 0, unstuck: 0, mantles: 0, slides: 0, jumps: 0 };
    this.virtualFloor = null;
    this._killCount = 0;
    this._resolvedSpawn = null;
    this._resolvedSpawnIndex = -1;
  }

  /* ------------------------------------------------------------ lifecycle */

  init(ctx) {
    this.ctx = ctx;
    this.virtualFloor = null;
    this._killCount = 0;
    /* If nothing in the world can be collided with (level collision not built
       yet, physics not wired), stand the player on a virtual plane at the
       spawn instead of dropping them through infinity. */
    if (!this.adapter.hasCollision) this.virtualFloor = this.spawnFeet(new THREE.Vector3()).y;
    this.resetToSpawn();
  }

  /**
   * Feet position for the player spawn.
   *
   * `ctx.level.spawnPoints` is authoritative when there is solid ground under
   * it. When there is not — a level still being built, a spawn marker over a
   * gap in the collision proxies — we search outward for the nearest walkable
   * surface at a similar height rather than dropping the player out of the
   * world. Deterministic: the same level always resolves to the same point.
   */
  spawnFeet(out = new THREE.Vector3(), index = 0) {
    if (this._resolvedSpawn && this._resolvedSpawnIndex === index) return out.copy(this._resolvedSpawn);
    const sp = this.ctx?.level?.spawnPoints?.[index];
    const base = _spawnBase.set(sp ? sp.x : 0, sp ? sp.y - this.cfg.eyeStand : 0, sp ? sp.z : 0);
    this._resolvedSpawn = this._findStandingSpot(base);
    this._resolvedSpawnIndex = index;
    return out.copy(this._resolvedSpawn);
  }

  /** Forget the cached spawn (call after the level rebuilds). */
  invalidateSpawn() { this._resolvedSpawn = null; }

  _findStandingSpot(base) {
    const cfg = this.cfg;
    const ad = this.adapter;
    const half = cfg.standHeight * 0.5;

    /** Cast down from `y` and return walkable feet, or null. */
    const probe = (x, z, fromY, far) => {
      _probeOrigin.set(x, fromY, z);
      const h = ad.raycast(_probeOrigin, _down, far);
      if (!h || h.normal.y < 0.55) return null;
      _spawnCand.set(x, h.point.y, z);
      /* capsuleFits raycasts internally and reuses the adapter's hit record,
         so read everything we need out of `h` before calling it. */
      if (!ad.capsuleFits(_spawnCand, cfg.standHeight, cfg.radius)) return null;
      return _spawnCand.y;
    };

    /* 1. straight down from just above the marker — the normal case */
    let y = probe(base.x, base.z, base.y + 2.0, 8.0);
    if (y !== null) return new THREE.Vector3(base.x, y, base.z);

    /* 2. spiral outward at the marker's own height, then through everything.
          Score by height error first, distance second. */
    const top = ad.bounds.max.y + 5;
    const far = (ad.bounds.max.y - ad.bounds.min.y) + 60;
    let best = null;
    for (const pass of [0, 1]) {
      for (let ring = 1; ring <= 26; ring++) {
        const radius = ring * 1.25;
        const n = 8 + ring * 4;
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          const x = base.x + Math.cos(a) * radius;
          const z = base.z + Math.sin(a) * radius;
          const hy = pass === 0
            ? probe(x, z, base.y + 2.0, 8.0)
            : probe(x, z, top, far);
          if (hy === null) continue;
          const score = Math.abs(hy - base.y) * 2 + radius * 0.25;
          if (!best || score < best.score) best = { x, y: hy, z, score };
        }
        if (best && pass === 0) return new THREE.Vector3(best.x, best.y, best.z);
      }
      if (best) return new THREE.Vector3(best.x, best.y, best.z);
    }

    /* 3. nothing walkable exists at all — stay put rather than fall forever */
    return base.clone();
  }

  resetToSpawn() {
    this.spawnFeet(this.spawn);
    this.teleport(this.spawn);
  }

  teleport(feet, yaw = this.yaw) {
    this.feet.copy(feet);
    this.prevFeet.copy(feet);
    this.lastSafe.copy(feet);
    this.velocity.set(0, 0, 0);
    this.yaw = yaw;
    this.grounded = true;
    this.wasGrounded = true;
    this.crouched = false;
    this.sliding = false;
    this.sprinting = false;
    this.tacSprinting = false;
    this.mantling = false;
    this.mantle.active = false;
    this.mantle.progress = 0;
    this.mantle.handWeight = 0;
    this.height = this.cfg.standHeight;
    this.slideT = 0;
    this.slideCooldown = 0;
    this.mantleCooldown = 0;
    this.jumpBuffer = 0;
    this.jumpCooldown = 0;
    this.coyote = this.cfg.coyoteTime;
    this.airTime = 0;
    this.bobPhase = 0;
    this.prevBobPhase = 0;
    this.stepIndex = 0;
    this.stamina = this.cfg.staminaMax;
    this.speed = 0;
    this.state = 'idle';
    this.fallPeakY = feet.y;
    this.lastFallHeight = 0;
    this._sprintWasPressed = false;
    this._crouchHeld = false;
    this.tacBlend = 0;
    this.adsBlend = 0;
    this.hitWall = false;
    // Settle onto the floor so we never start floating or buried.
    this._settle();
  }

  _settle() {
    const cfg = this.cfg;
    const half = this.height * 0.5;
    _centre.set(this.feet.x, this.feet.y + half, this.feet.z);
    const res = this.adapter.capsuleMove(_centre, half, cfg.radius, _delta.set(0, -0.001, 0), {
      stepHeight: 0, wasGrounded: true, groundSnap: 2.5, groundProbe: 2.5,
    });
    if (res && Number.isFinite(res.position.y)) {
      this.feet.set(res.position.x, res.position.y - half, res.position.z);
      this.prevFeet.copy(this.feet);
      this.lastSafe.copy(this.feet);
      this.grounded = res.grounded;
    }
  }

  get halfHeight() { return this.height * 0.5; }
  get eyeTarget() {
    if (this.sliding) return this.cfg.eyeSlide;
    if (this.crouched) return this.cfg.eyeCrouch;
    return this.cfg.eyeStand;
  }

  /* ------------------------------------------------------------- helpers */

  basis(yaw = this.yaw) {
    _fwd.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    _right.set(Math.cos(yaw), 0, -Math.sin(yaw));
  }

  horizontalSpeed() {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  /* ---------------------------------------------------------------- step */

  fixed(dt, input) {
    const cfg = this.cfg;
    const ev = this.ctx?.events;

    this.prevFeet.copy(this.feet);
    this.prevBobPhase = this.bobPhase;
    this.landedThisStep = false;
    this.wasGrounded = this.grounded;
    this.steppedUp = 0;

    this.adapter.maybeRebuild(dt);

    /* ---- timers ---- */
    this.jumpCooldown = Math.max(0, this.jumpCooldown - dt);
    this.slideCooldown = Math.max(0, this.slideCooldown - dt);
    this.mantleCooldown = Math.max(0, this.mantleCooldown - dt);
    this.sprintTapTimer = Math.max(0, this.sprintTapTimer - dt);
    this.jumpBuffer = Math.max(0, this.jumpBuffer - dt);
    if (this.grounded) this.coyote = cfg.coyoteTime; else this.coyote = Math.max(0, this.coyote - dt);
    if (input.jumpEdge) this.jumpBuffer = cfg.jumpBuffer;

    const ads = clamp(this.ctx?.weapons?.ads ?? 0, 0, 1);
    this.adsBlend = ads;

    /* ---- mantle owns everything while it runs ---- */
    if (this.mantling) {
      this._stepMantle(dt);
      this._finishStep(dt, input, ev);
      return;
    }

    /* ---- sprint / tac sprint ---- */
    this._updateSprint(dt, input, ads);

    /* ---- crouch, slide entry ---- */
    this._updateCrouch(dt, input);

    /* ---- mantle / vault trigger ---- */
    if (this.mantleCooldown <= 0 && !this.sliding) {
      const wantJumpMantle = this.jumpBuffer > 0;
      const wantAutoVault = this.grounded && this.hitWall && this.horizontalSpeed() > cfg.mantleMinSpeedAuto && input.forward > 0.4;
      if (wantJumpMantle || wantAutoVault) {
        const led = this._probeLedge(wantAutoVault && !wantJumpMantle);
        if (led) {
          this._beginMantle(led);
          this.jumpBuffer = 0;
          this._stepMantle(dt);
          this._finishStep(dt, input, ev);
          return;
        }
      }
    }

    /* ---- jump ---- */
    if (this.jumpBuffer > 0 && this.jumpCooldown <= 0 && (this.grounded || this.coyote > 0)) {
      this._jump(input);
    }

    /* ---- acceleration ---- */
    if (this.sliding) this._accelSlide(dt, input);
    else if (this.grounded) this._accelGround(dt, input, ads);
    else this._accelAir(dt, input, ads);

    /* ---- gravity (leapfrog: half before the move, half after, so the
           discrete jump arc matches the analytic apex exactly) ---- */
    if (!this.grounded) this.velocity.y -= cfg.gravity * dt * 0.5;
    else if (this.velocity.y < 0) this.velocity.y = 0;

    /* ---- integrate through the physics contract ---- */
    this._move(dt);

    if (!this.grounded) this.velocity.y -= cfg.gravity * dt * 0.5;
    else if (this.velocity.y < 0) this.velocity.y = 0;

    /* ---- airborne bookkeeping / landing ---- */
    if (!this.grounded) {
      if (this.wasGrounded) this.fallPeakY = this.feet.y;
      else this.fallPeakY = Math.max(this.fallPeakY, this.feet.y);
    }
    if (this.grounded && !this.wasGrounded) this._land(ev);
    if (!this.grounded) this.airTime += dt; else this.airTime = 0;

    this._finishStep(dt, input, ev);
  }

  /* --------------------------------------------------------------- gaits */

  _updateSprint(dt, input, ads) {
    const cfg = this.cfg;
    const pressed = !!input.sprint;
    if (pressed && !this._sprintWasPressed) {
      if (this.sprintTapTimer > 0 && this.stamina > 0.08) this.tacSprinting = true;
      this.sprintTapTimer = cfg.tacSprintTapWindow;
    }
    this._sprintWasPressed = pressed;

    const wantsForward = input.forward > 0.45;
    const canSprint = pressed && wantsForward && !this.crouched && !this.sliding && ads < 0.5;
    this.sprinting = canSprint;
    if (!canSprint) this.tacSprinting = false;

    if (this.tacSprinting) {
      this.stamina = Math.max(0, this.stamina - cfg.staminaDrainTac * dt);
      this.staminaHold = cfg.staminaRegenDelay;
      if (this.stamina <= 0) this.tacSprinting = false;
      this.tacBlend = Math.min(1, this.tacBlend + dt / cfg.tacSprintWindup);
    } else {
      this.staminaHold = Math.max(0, this.staminaHold - dt);
      if (this.staminaHold <= 0) this.stamina = Math.min(cfg.staminaMax, this.stamina + cfg.staminaRegen * dt);
      this.tacBlend = Math.max(0, this.tacBlend - dt / (cfg.tacSprintWindup * 0.8));
    }
  }

  /** Target ground speed for the current gait, including directional scaling. */
  targetSpeed(input, ads) {
    const cfg = this.cfg;
    let base;
    if (this.crouched) base = cfg.speedCrouch;
    else if (this.sprinting) base = cfg.speedSprint + (cfg.speedTacSprint - cfg.speedSprint) * this.tacBlend;
    else base = cfg.speedWalk;

    if (ads > 0.02 && !this.sprinting && !this.crouched) {
      base = base + (cfg.speedAds - base) * ads;
    }

    const f = input.forward, r = input.right;
    const m = Math.hypot(f, r);
    if (m > 1e-4) {
      const nf = f / m, nr = r / m;
      let scale = 1;
      if (nf < 0) scale *= 1 + (cfg.scaleBack - 1) * -nf;
      scale *= 1 + (cfg.scaleStrafe - 1) * Math.abs(nr);
      base *= scale;
    }
    return base;
  }

  _wishDir(input) {
    this.basis();
    _wish.set(0, 0, 0);
    _wish.addScaledVector(_fwd, input.forward);
    _wish.addScaledVector(_right, input.right);
    const len = _wish.length();
    if (len > 1e-5) _wish.divideScalar(len);
    return len > 1e-5 ? Math.min(1, len) : 0;
  }

  _accelGround(dt, input, ads) {
    const cfg = this.cfg;
    const mag = this._wishDir(input);
    const wishSpeed = mag > 0 ? this.targetSpeed(input, ads) * mag : 0;

    /* friction first (Quake style, with a stop-speed floor for crisp stops) */
    const sp = this.horizontalSpeed();
    if (sp > 1e-4) {
      const fr = this.crouched ? cfg.frictionCrouch : cfg.frictionGround;
      const control = Math.max(sp, cfg.frictionStopSpeed);
      let drop = control * fr * dt;
      const newSp = Math.max(0, sp - drop);
      const k = newSp / sp;
      this.velocity.x *= k;
      this.velocity.z *= k;
    }

    if (wishSpeed <= 0) return;
    const cur = this.velocity.x * _wish.x + this.velocity.z * _wish.z;
    const add = wishSpeed - cur;
    if (add <= 0) return;
    const accelSpeed = Math.min(cfg.accelGround * dt, add);
    this.velocity.x += _wish.x * accelSpeed;
    this.velocity.z += _wish.z * accelSpeed;
  }

  _accelAir(dt, input, ads) {
    const cfg = this.cfg;
    const mag = this._wishDir(input);
    /* very light air drag so long jumps do not feel frictionless */
    const sp = this.horizontalSpeed();
    if (sp > 1e-4) {
      const k = Math.max(0, 1 - cfg.frictionAir * dt);
      this.velocity.x *= k; this.velocity.z *= k;
    }
    if (mag <= 0) return;
    const wishSpeed = Math.min(this.targetSpeed(input, ads) * mag, cfg.airWishSpeedCap);
    const cur = this.velocity.x * _wish.x + this.velocity.z * _wish.z;
    const add = wishSpeed - cur;
    if (add <= 0) return;
    const accelSpeed = Math.min(cfg.accelAir * dt, add);
    this.velocity.x += _wish.x * accelSpeed;
    this.velocity.z += _wish.z * accelSpeed;
  }

  _accelSlide(dt, input) {
    const cfg = this.cfg;
    this.slideT += dt;
    const u = clamp(this.slideT / cfg.slideDuration, 0, 1);
    const fr = cfg.slideFrictionStart + (cfg.slideFrictionEnd - cfg.slideFrictionStart) * Math.pow(u, cfg.slideFrictionCurve);

    let sp = this.horizontalSpeed();
    if (sp > 1e-4) {
      const k = Math.max(0, 1 - fr * dt);
      this.velocity.x *= k;
      this.velocity.z *= k;
      sp *= k;
    }

    /* limited steering — you can curve a slide, not turn it around */
    const mag = this._wishDir(input);
    if (mag > 0 && sp > 0.2) {
      const cs = this.slideDir;
      const cross = cs.x * _wish.z - cs.z * _wish.x;
      const dot = clamp(cs.x * _wish.x + cs.z * _wish.z, -1, 1);
      const ang = Math.atan2(cross, dot);
      const turn = clamp(ang, -cfg.slideSteer * dt, cfg.slideSteer * dt);
      const c = Math.cos(turn), s = Math.sin(turn);
      const nx = cs.x * c - cs.z * s;
      const nz = cs.x * s + cs.z * c;
      cs.set(nx, 0, nz).normalize();
      this.velocity.x = cs.x * sp;
      this.velocity.z = cs.z * sp;
    } else if (sp > 1e-4) {
      this.slideDir.set(this.velocity.x / sp, 0, this.velocity.z / sp);
    }

    if (u >= 1 || sp < cfg.slideExitSpeed) this._endSlide(false);
  }

  /* --------------------------------------------------------- crouch/slide */

  _updateCrouch(dt, input) {
    const cfg = this.cfg;
    const want = !!input.crouch;

    if (this.sliding) {
      if (!this.grounded && this.airTime > 0.25) this._endSlide(false);
      return;
    }

    if (want && input.crouchEdge && this.grounded && this.slideCooldown <= 0 &&
        this.horizontalSpeed() >= cfg.slideMinSpeed && !this.crouched) {
      this._startSlide();
      return;
    }

    if (want) {
      this.crouched = true;
      this.height = cfg.crouchHeight;
    } else if (this.crouched) {
      if (this._canStand()) {
        this.crouched = false;
        this.height = cfg.standHeight;
      }
    }
  }

  _canStand() {
    return this.adapter.capsuleFits(this.feet, this.cfg.standHeight, this.cfg.radius);
  }

  _startSlide() {
    const cfg = this.cfg;
    const sp = this.horizontalSpeed();
    if (sp < 1e-3) return;
    this.slideDir.set(this.velocity.x / sp, 0, this.velocity.z / sp);
    const boost = Math.min(cfg.slideSpeedCap, Math.max(sp * cfg.slideBoost, cfg.slideBoostMin));
    this.velocity.x = this.slideDir.x * boost;
    this.velocity.z = this.slideDir.z * boost;
    this.slideEntrySpeed = boost;
    this.sliding = true;
    this.crouched = true;
    this.height = cfg.crouchHeight;
    this.slideT = 0;
    this.sprinting = false;
    this.tacSprinting = false;
    this.debugCounters.slides++;
  }

  _endSlide(cancelled) {
    if (!this.sliding) return;
    this.sliding = false;
    this.slideT = 0;
    this.slideCooldown = this.cfg.slideCooldown;
    this.crouched = !cancelled && !!this._crouchHeld;
    if (!this.crouched && this._canStand()) this.height = this.cfg.standHeight;
    else { this.crouched = true; this.height = this.cfg.crouchHeight; }
  }

  /* ---------------------------------------------------------------- jump */

  _jump(input) {
    const cfg = this.cfg;
    const impulse = Math.sqrt(2 * cfg.gravity * cfg.jumpApex);
    if (this.sliding) {
      this._endSlide(true);
      this.velocity.x *= cfg.slideJumpKeep;
      this.velocity.z *= cfg.slideJumpKeep;
    } else if (this.crouched && this._canStand()) {
      this.crouched = false;
      this.height = cfg.standHeight;
    }
    this.velocity.y = impulse;
    if (this.sprinting && input.forward > 0.4) {
      this.basis();
      this.velocity.x += _fwd.x * cfg.jumpForwardBoost;
      this.velocity.z += _fwd.z * cfg.jumpForwardBoost;
    }
    this.grounded = false;
    this.coyote = 0;
    this.jumpBuffer = 0;
    this.jumpCooldown = cfg.jumpCooldown;
    this.debugCounters.jumps++;
  }

  _land(ev) {
    const cfg = this.cfg;
    const impact = Math.max(0, -this.lastVy);
    const fallHeight = Math.max(0, this.fallPeakY - this.feet.y);
    this.lastFallHeight = fallHeight;
    this.pendingLandImpact = Math.max(this.pendingLandImpact, impact);
    this.landedThisStep = true;
    this.airTime = 0;
    this._sampleSurface(true);
    ev?.emit('player:land', {
      position: this.feet.clone(), impact, fallHeight, surface: this.surface,
    });
    if (fallHeight > cfg.fallSafeHeight && this.onFallDamage) {
      if (fallHeight >= cfg.fallLethalHeight) this.onFallDamage(1e4);
      else {
        const u = (fallHeight - cfg.fallSafeHeight) / (cfg.fallLethalHeight - cfg.fallSafeHeight);
        this.onFallDamage(u * cfg.fallDamageMax);
      }
    }
  }

  /* -------------------------------------------------------------- mantle */

  /**
   * Look for a ledge in front of the player.
   * @param {boolean} lowOnly restrict to vault-height ledges (auto-vault)
   */
  _probeLedge(lowOnly = false) {
    const cfg = this.cfg;
    const ad = this.adapter;
    this.basis();
    const reach = cfg.radius + cfg.mantleReach;

    let hit = null;
    const heights = lowOnly ? [0.42, 0.75] : [cfg.mantleProbeHeight, 0.62, 1.45];
    for (const h of heights) {
      _probeOrigin.set(this.feet.x, this.feet.y + h, this.feet.z);
      const r = ad.raycast(_probeOrigin, _fwd, reach);
      if (r && Math.abs(r.normal.y) < 0.5) {
        _hitPoint.copy(r.point);
        _hitNormal.copy(r.normal);
        hit = true;
        break;
      }
    }
    if (!hit) return null;

    /* Walk up: find the top surface just past the wall face. */
    const maxH = lowOnly ? cfg.vaultMaxHeight : cfg.mantleMaxHeight;
    _probeOrigin.copy(_hitPoint).addScaledVector(_fwd, 0.14);
    _probeOrigin.y = this.feet.y + maxH + 0.30;
    const top = ad.raycast(_probeOrigin, _tmp.set(0, -1, 0), maxH + 0.55);
    if (!top) return null;
    _topPoint.copy(top.point);
    _topNormal.copy(top.normal);
    if (_topNormal.y < 0.6) return null;

    const height = _topPoint.y - this.feet.y;
    if (height < cfg.mantleMinHeight || height > maxH) return null;

    /* Landing spot past the lip. */
    const target = new THREE.Vector3(_topPoint.x, _topPoint.y, _topPoint.z)
      .addScaledVector(_fwd, cfg.mantleClearDepth);

    /* Clearance: prefer standing, fall back to crouched, else refuse. */
    let exitCrouch = false;
    if (!ad.capsuleFits(target, cfg.standHeight, cfg.radius)) {
      if (!ad.capsuleFits(target, cfg.crouchHeight, cfg.radius)) return null;
      exitCrouch = true;
    }

    const kind = height <= cfg.vaultMaxHeight ? 'vault' : 'mantle';
    return {
      kind,
      height,
      target,
      ledge: new THREE.Vector3(_topPoint.x, _topPoint.y, _topPoint.z),
      normal: new THREE.Vector3(-_fwd.x, 0, -_fwd.z).normalize(),
      exitCrouch,
    };
  }

  _beginMantle(led) {
    const cfg = this.cfg;
    const m = this.mantle;
    m.active = true;
    m.kind = led.kind;
    m.progress = 0;
    m.elapsed = 0;
    m.duration = led.kind === 'vault' ? cfg.vaultDuration : cfg.mantleDuration;
    m.start.copy(this.feet);
    m.target.copy(led.target);
    m.ledge.copy(led.ledge);
    m.normal.copy(led.normal);
    m.height = led.height;
    m.handWeight = 0;
    this._mantleExitCrouch = led.exitCrouch;
    this.mantling = true;
    this.sliding = false;
    this.sprinting = false;
    this.velocity.set(0, 0, 0);
    this.grounded = false;
    this.debugCounters.mantles++;
    this.ctx?.events?.emit('player:mantle', { kind: led.kind, height: led.height, ledge: m.ledge.clone() });
  }

  _stepMantle(dt) {
    const cfg = this.cfg;
    const m = this.mantle;
    m.elapsed += dt;
    const p = clamp(m.elapsed / m.duration, 0, 1);
    m.progress = p;

    /* Up first, then forward — the classic mantle arc. */
    const yE = smoothstep(0, 0.62, p);
    const xzE = smoothstep(0.26, 1.0, p);
    const overshoot = Math.sin(Math.PI * clamp((p - 0.45) / 0.5, 0, 1)) * 0.035;

    this.feet.y = m.start.y + (m.target.y - m.start.y) * yE + overshoot;
    this.feet.x = m.start.x + (m.target.x - m.start.x) * xzE;
    this.feet.z = m.start.z + (m.target.z - m.start.z) * xzE;

    /* Hand IK targets on the lip, for the weapons/animation team. */
    _tmp.copy(m.normal).cross(UP).normalize();       // ledge tangent
    _tmp2.copy(m.ledge).addScaledVector(m.normal, 0.04);
    m.handL.copy(_tmp2).addScaledVector(_tmp, -0.27);
    m.handR.copy(_tmp2).addScaledVector(_tmp, 0.27);
    m.handL.y = m.ledge.y + 0.03;
    m.handR.y = m.ledge.y + 0.03;
    m.handWeight = smoothstep(0.04, 0.24, p) * (1 - smoothstep(0.58, 0.86, p));

    /* Velocity is derived so the camera rig and audio see real motion. */
    this.velocity.set(
      (this.feet.x - this.prevFeet.x) / dt,
      (this.feet.y - this.prevFeet.y) / dt,
      (this.feet.z - this.prevFeet.z) / dt,
    );

    if (p >= 1) this._endMantle();
  }

  _endMantle() {
    const cfg = this.cfg;
    const m = this.mantle;
    this.feet.copy(m.target);
    m.active = false;
    m.progress = 1;
    m.handWeight = 0;
    this.mantling = false;
    this.mantleCooldown = cfg.mantleCooldown;

    if (this._mantleExitCrouch) { this.crouched = true; this.height = cfg.crouchHeight; }
    else { this.crouched = false; this.height = cfg.standHeight; }

    /* Refuse to end inside geometry — back out to where we started. */
    if (!this.adapter.capsuleFits(this.feet, this.height, cfg.radius)) {
      this.feet.copy(m.start);
      this.debugCounters.unstuck++;
    }

    this.basis();
    this.velocity.set(_fwd.x * cfg.mantleExitSpeed, 0, _fwd.z * cfg.mantleExitSpeed);
    this.grounded = false;
    this.coyote = cfg.coyoteTime;
    this.lastSafe.copy(this.feet);
    this.ctx?.events?.emit('player:mantle', { kind: m.kind, phase: 'end', ledge: m.ledge.clone() });
  }

  /* ----------------------------------------------------------- integrate */

  _move(dt) {
    const cfg = this.cfg;
    const half = this.halfHeight;
    const vy = this.velocity.y;
    this.lastVy = vy;

    const sp = this.velocity.length();
    if (sp > cfg.maxSafeSpeed) this.velocity.multiplyScalar(cfg.maxSafeSpeed / sp);

    _delta.copy(this.velocity).multiplyScalar(dt);
    _centre.set(this.feet.x, this.feet.y + half, this.feet.z);

    const settled = this.wasGrounded && vy <= 0.01;
    const res = this.adapter.capsuleMove(_centre, half, cfg.radius, _delta, {
      stepHeight: cfg.stepHeight,
      wasGrounded: settled,
      groundSnap: settled ? cfg.groundSnap : 0,
      groundProbe: cfg.groundProbe,
      maxSlope: cfg.maxGroundAngleCos,
    });

    const nx = res.position.x, ny = res.position.y - half, nz = res.position.z;
    const dx = nx - this.feet.x, dy = ny - this.feet.y, dz = nz - this.feet.z;

    this.feet.set(nx, ny, nz);
    /* Never accept "grounded" while we are climbing under our own power —
       a naive ground probe would otherwise cancel the jump on frame one. */
    this.grounded = !!res.grounded && vy <= 0.05;
    this.groundNormal.copy(res.groundNormal);
    this.hitWall = !!res.hitWall;
    this.steppedUp = res.steppedUp || 0;

    /* Horizontal velocity is re-derived from the actual displacement so the
       controller slides along walls without ever needing the contact normal —
       this works with any conforming capsuleMove implementation. */
    const wantX = this.velocity.x * dt, wantZ = this.velocity.z * dt;
    const wantLen = Math.hypot(wantX, wantZ);
    const gotLen = Math.hypot(dx, dz);
    if (wantLen > 1e-6 && gotLen <= wantLen * 1.25 + 1e-4) {
      this.velocity.x = dx / dt;
      this.velocity.z = dz / dt;
    }

    /* Vertical: ground stops falls, ceilings stop rises. */
    if (this.grounded && this.velocity.y <= 0) {
      this.velocity.y = 0;
    } else if (this.velocity.y > 0 && this.steppedUp <= 1e-4) {
      const wantY = this.velocity.y * dt;
      if (dy < wantY - 1e-4) this.velocity.y = Math.min(this.velocity.y, 0);
    }

    this._safety();
  }

  _safety() {
    const cfg = this.cfg;
    const f = this.feet;

    /* Degenerate-world floor. Never active on a level with real collision. */
    if (this.virtualFloor !== null && f.y < this.virtualFloor) {
      f.y = this.virtualFloor;
      if (this.velocity.y < 0) this.velocity.y = 0;
      this.grounded = true;
      this.groundNormal.set(0, 1, 0);
    }
    if (!Number.isFinite(f.x) || !Number.isFinite(f.y) || !Number.isFinite(f.z)) {
      f.copy(this.lastSafe);
      this.velocity.set(0, 0, 0);
      this.debugCounters.unstuck++;
      return;
    }

    const killY = this.adapter.bounds.min.y - cfg.killPlaneMargin;
    if (f.y < killY) {
      this._killCount++;
      /* Falling out repeatedly means the spawn itself has no ground under it —
         pin a virtual floor so the player is stable instead of looping. */
      if (this._killCount >= 2 && this.virtualFloor === null) {
        this.virtualFloor = this.spawnFeet(new THREE.Vector3()).y;
      }
      this.spawnFeet(this.spawn);
      this.teleport(this.spawn);
      this.debugCounters.respawns++;
      this.ctx?.events?.emit('player:respawn', { position: f.clone(), reason: 'out-of-world' });
      return;
    }

    const half = this.halfHeight;
    _centre.set(f.x, f.y + half, f.z);
    let stuck = false;
    if (this.adapter.usingFallback) {
      stuck = this.adapter.penetration(_centre, half, cfg.radius) > 0.09;
    } else {
      const phys = this.ctx?.physics;
      if (typeof phys?.pointInSolid === 'function') {
        try { stuck = !!phys.pointInSolid(_centre); } catch { stuck = false; }
      }
    }
    if (stuck) {
      f.copy(this.lastSafe);
      this.velocity.set(0, 0, 0);
      this.debugCounters.unstuck++;
    } else if (this.grounded && this.horizontalSpeed() < 14) {
      this.lastSafe.copy(f);
    }
  }

  /* -------------------------------------------------------- step wrap-up */

  _finishStep(dt, input, ev) {
    const cfg = this.cfg;
    this._crouchHeld = !!input.crouch;
    this.speed = this.horizontalSpeed();

    /* footstep phase: pi per step, phase-locked to the figure-of-eight bob */
    if (this.grounded && !this.sliding && !this.mantling && this.speed > 0.55) {
      const stride = this.crouched ? cfg.strideCrouch
        : this.sprinting ? cfg.strideSprint
          : this.adsBlend > 0.5 ? cfg.strideAds : cfg.strideWalk;
      this.bobPhase += Math.PI * (this.speed / stride) * dt;
    } else {
      /* ease the phase back to a neutral pose instead of freezing mid-stride */
      const tgt = Math.ceil(this.bobPhase / Math.PI) * Math.PI;
      this.bobPhase += Math.min(tgt - this.bobPhase, Math.PI * 1.6 * dt);
    }

    if (this.grounded && !this.sliding && !this.mantling && this.speed > 0.55) {
      const a = Math.floor((this.prevBobPhase - cfg.footstepPhase) / Math.PI);
      const b = Math.floor((this.bobPhase - cfg.footstepPhase) / Math.PI);
      if (b > a) {
        for (let i = a + 1; i <= b; i++) {
          this.stepIndex++;
          this._sampleSurface(true);
          ev?.emit('player:footstep', {
            position: this.feet.clone(),
            surface: this.surface,
            foot: this.stepIndex % 2 === 0 ? 'left' : 'right',
            speed: this.speed,
            gait: this.state,
          });
        }
      }
    }

    this._surfaceTimer -= dt;
    if (this._surfaceTimer <= 0) this._sampleSurface(true);

    const s = this._classify();
    if (s !== this.state) {
      this.state = s;
      ev?.emit('player:state', {
        state: s,
        speed: this.speed,
        grounded: this.grounded,
        crouched: this.crouched,
      });
    }
  }

  _classify() {
    if (this.mantling) return 'mantle';
    if (this.sliding) return 'slide';
    if (!this.grounded) return 'air';
    if (this.crouched) return 'crouch';
    if (this.sprinting && this.speed > this.cfg.speedWalk * 0.9) return 'sprint';
    if (this.speed < 0.25) return 'idle';
    if (this.speed < this.cfg.speedWalk * 0.62) return 'walk';
    return 'run';
  }

  _sampleSurface(force = false) {
    this._surfaceTimer = 0.25;
    _probeOrigin.set(this.feet.x, this.feet.y + 0.25, this.feet.z);
    const h = this.adapter.raycast(_probeOrigin, _tmp.set(0, -1, 0), 0.9);
    if (h) this.surface = h.surface || this.adapter.surfaceAt(h.point, 'concrete');
  }
}

export default Movement;
